import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import baseUrl from 'Base_Url/baseUrl';
import {
  Box,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useDisclosure,
  Icon,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  useToast,
  Flex,
  Tooltip,
  IconButton,
  Skeleton,
  CloseButton,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon } from '@chakra-ui/icons';
import { FaBuilding } from 'react-icons/fa';
import Select from 'react-select';
import { FaMapMarkedAlt } from 'react-icons/fa';
import { useColorMode } from '@chakra-ui/react';
import { RiDeleteBin2Line } from 'react-icons/ri';
import Swal from 'sweetalert2';
import { MdEditSquare } from 'react-icons/md';

const DistrictSection = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newDistrict, setNewDistrict] = useState('');
  const [divisionId, setDivisionId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const toast = useToast();
  const token = localStorage.getItem('token');
  const [districtLoading, setDistrictLoading] = useState(false);
  const deleteTimeouts = useRef({});
  const [undoDistrict, setUndoDistrict] = useState(null);


  const [isEditing, setIsEditing] = useState(false);
  const [editDistrictId, setEditDistrictId] = useState(null);

  const fetchDistricts = useCallback(
    async (search = '') => {
      setDistrictLoading(true);
      try {
        const response = await axios.get(`${baseUrl}/master/all-districts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search },
        });
        const districtsArray = response.data?.data?.data || [];
        districtsArray.sort((a, b) => a.name.localeCompare(b.name));
        setDistricts(districtsArray);
      } catch (error) {
        toast({
          title: 'Error fetching districts',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setDistrictLoading(false);
      }
    },
    [token, toast],
  );

  useEffect(() => {
    fetchDistricts(searchInput);
  }, [searchInput, fetchDistricts]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => { }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const fetchDivisions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/master/all-divisions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const divisionsArray = response.data?.data?.data || [];
      divisionsArray.sort((a, b) => a.name.localeCompare(b.name));
      setDivisions(divisionsArray);
    } catch (error) {
      toast({
        title: 'Error fetching divisions',
        description: error?.response?.data?.message || 'Something went wrong.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);


  const handleOpenModal = (district = null) => {
    fetchDivisions();
    if (district) {
      setIsEditing(true);
      setEditDistrictId(district._id);
      setNewDistrict(district.name);
      setDivisionId(district.division?._id || '');
    } else {
      setIsEditing(false);
      setEditDistrictId(null);
      setNewDistrict('');
      setDivisionId('');
    }
    onOpen();
  };

  const handleAddDistrict = useCallback(async () => {
    if (newDistrict.trim() === '' || !divisionId) return;
    setLoading(true);
    try {
      await axios.post(
        `${baseUrl}/master/add-district`,
        {
          name: newDistrict.trim(),
          division: divisionId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'District added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchDistricts();
      handleCancel();
    } catch (error) {
      toast({
        title: 'Failed to add district',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [newDistrict, divisionId, token, toast, fetchDistricts]);


  const handleUpdateDistrict = useCallback(async () => {
    if (newDistrict.trim() === '' || !divisionId || !editDistrictId) return;
    setLoading(true);
    try {
      await axios.put(
        `${baseUrl}/master/edit-district/${editDistrictId}`,
        {
          name: newDistrict.trim(),
          division: divisionId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'District updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchDistricts();
      handleCancel();
    } catch (error) {
      toast({
        title: 'Failed to update district',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [newDistrict, divisionId, editDistrictId, token, toast, fetchDistricts]);

  const handleCancel = useCallback(() => {
    setNewDistrict('');
    setDivisionId('');
    setIsEditing(false);
    setEditDistrictId(null);
    onClose();
  }, [onClose]);

  const handleDeleteDistrict = useCallback(
    (id) => {
      const districtToDelete = districts.find((d) => d._id === id);

      Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this District!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1f580cff',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.isConfirmed) {
          setDistricts((prev) => prev.filter((d) => d._id !== id));
          setUndoDistrict(districtToDelete);

          const toastId = `delete-toast-${id}`;

          toast({
            id: toastId,
            position: 'bottom-left',
            duration: 5000,
            render: ({ onClose }) => (
              <Flex
                align="center"
                justify="space-between"
                p={3}
                bg="gray.700"
                color="white"
                borderRadius="md"
                boxShadow="lg"
              >
                <Text fontSize="sm" noOfLines={1} mr={3}>
                  "{districtToDelete.name}" deleted
                </Text>
                <Button
                  variant="ghost"
                  colorScheme="blue"
                  size="sm"
                  onClick={() => {
                    clearTimeout(deleteTimeouts.current[id]);
                    delete deleteTimeouts.current[id];

                    setDistricts((prev) =>
                      [...prev, districtToDelete].sort((a, b) =>
                        a.name.localeCompare(b.name),
                      ),
                    );
                    setUndoDistrict(null);
                    onClose();
                  }}
                >
                  Undo
                </Button>
                <CloseButton onClick={onClose} color="whiteAlpha.800" />
              </Flex>
            ),
          });

          deleteTimeouts.current[id] = setTimeout(async () => {
            try {
              await axios.delete(`${baseUrl}/master/delete-district/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setUndoDistrict(null);
            } catch (error) {
              toast({
                title: 'Error deleting District',
                status: 'error',
                duration: 3000,
                isClosable: true,
              });
            }
          }, 5000);
        }
      });
    },
    [token, toast, districts],
  );

  useEffect(() => {
    return () => {
      Object.values(deleteTimeouts.current).forEach(clearTimeout);
    };
  }, []);


  const bgBox = useColorModeValue('white', 'gray.900');
  const bgModalBody = useColorModeValue('white', 'gray.800');
  const modalFooterBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const boxShadow = useColorModeValue('md', 'dark-lg');
  const rowHoverBg = useColorModeValue('#f1f5f9', 'gray.600');
  const inputTextColor = useColorModeValue('gray.800', 'white');
  const { colorMode } = useColorMode();

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      background: colorMode === 'dark' ? '#2D3748' : 'white',
      color: colorMode === 'dark' ? 'white' : 'black',
      borderColor: colorMode === 'dark' ? '#4A5568' : '#CBD5E0',
    }),
    menu: (base) => ({
      ...base,
      background: colorMode === 'dark' ? '#1A202C' : 'white',
      color: colorMode === 'dark' ? 'white' : 'black',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? colorMode === 'dark'
          ? '#2B6CB0'
          : '#3182CE'
        : state.isFocused
          ? colorMode === 'dark'
            ? '#2D3748'
            : '#E2E8F0'
          : 'transparent',
      color: state.isSelected
        ? 'white'
        : colorMode === 'dark'
          ? 'white'
          : 'black',
      cursor: 'pointer',
    }),
    singleValue: (base) => ({
      ...base,
      color: colorMode === 'dark' ? 'white' : 'black',
    }),
    input: (base) => ({
      ...base,
      color: colorMode === 'dark' ? 'white' : 'black',
    }),
  };

  return (
    <Box
      p={4}
      bg={bgBox}
      shadow={boxShadow}
      borderRadius="md"
      border="1px solid"
      borderColor={borderColor}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Flex align="center" gap={2}>
          <Icon as={FaMapMarkedAlt} boxSize={5} color="#4B80DE" />
          <Heading size="md">Districts</Heading>
        </Flex>
        <Tooltip label="Add Districts" hasArrow>
          <IconButton
            colorScheme="blue"
            size="sm"
            onClick={() => handleOpenModal()}
            icon={<AddIcon />}
            aria-label="Add Districts"
            rounded="full"
            _hover={{ bg: 'blue.600' }}
          />
        </Tooltip>
      </Box>


      <Flex mb={4} gap={4} wrap="wrap">
        <InputGroup width="35%" size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search Districts"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            color={inputTextColor}
          />
        </InputGroup>
      </Flex>

      <Box
        bg={useColorModeValue('white', 'gray.800')}
        rounded="lg"
        overflow="hidden"
        mt={4}
        maxH="260px"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E0',
            borderRadius: '6px',
          },
        }}
      >
        <Table variant="simple" size="sm">
          <Thead
            position="sticky"
            top={0}
            zIndex={1}
            bgGradient="linear(to-r, blue.400, green.400)"
          >
            <Tr>
              <Th textAlign="center" color="white" fontSize="xs">
                Sr.No.
              </Th>
              <Th color="white" fontSize="xs">
                District
              </Th>
              <Th color="white" fontSize="xs">
                Division
              </Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {districtLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  <Td>
                    <Skeleton height="10px" />
                  </Td>
                  <Td>
                    <Skeleton height="10px" />
                  </Td>
                  <Td>
                    <Skeleton height="10px" />
                  </Td>
                  <Td>
                    <Skeleton height="10px" />
                  </Td>
                </Tr>
              ))
            ) : districts.length === 0 ? (
              <Tr>
                <Td colSpan={3} textAlign="center" py={6}>
                  <Text color="gray.400">No Districts Found</Text>
                </Td>
              </Tr>
            ) : (
              districts.map((district, index) => (
                <Tr
                  key={district._id}
                  _hover={{ bg: rowHoverBg }}
                  transition="background 0.2s"
                >
                  <Td textAlign="center" fontSize={'12px'}>
                    {index + 1}
                  </Td>
                  <Td fontSize={'12px'}>{district.name.toUpperCase()}</Td>
                  <Td fontSize={'12px'}>
                    {district.division?.name.toUpperCase()}
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      {/* <IconButton
                        icon={<MdEditSquare />}
                        aria-label="Edit"
                        variant="solid"
                        size="sm"
                        colorScheme="blue"
                        isRound
                        onClick={() => handleOpenModal(district)}
                      /> */}

                      <IconButton
                        icon={<MdEditSquare />}
                        aria-label="Edit"
                        variant="ghost"
                        size="sm"
                        color="blue.500"
                        _hover={{ bg: 'blue.50', color: 'blue.600' }}
                        onClick={() => handleOpenModal(district)}
                      />
                      {/* <IconButton
                        icon={<RiDeleteBin2Line />}
                        aria-label="Delete"
                        variant="ghost"
                        size="sm"
                        color="red.500"
                        _hover={{ color: 'blue.600' }}
                        onClick={() => handleDeleteDistrict(district._id)}
                      /> */}
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>


      <Modal isOpen={isOpen} onClose={handleCancel} isCentered>
        <ModalOverlay />
        <ModalContent rounded="lg" shadow="lg" overflow="hidden">
          <Box bg="blue.600" py={1} px={2} textAlign="center" roundedTop="lg">
            <ModalHeader
              fontSize="2xl"
              fontWeight="bold"
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={AddIcon} mr={2} />
              {isEditing ? 'Edit District' : 'Add New District'}
            </ModalHeader>
            <ModalCloseButton color="white" />
          </Box>
          <ModalBody bg={bgModalBody} px={6} py={4}>
            <Text fontSize="lg" fontWeight="bold" color="blue.800" mb={4}>
              <Icon as={FaBuilding} mr={2} />
              District Details
            </Text>

            <FormControl isRequired>
              <FormLabel>Division</FormLabel>
              <Select
                options={divisions.map((d) => ({
                  label: d.name,
                  value: d._id,
                }))}
                placeholder="Select Division"
                value={
                  divisions.find((d) => d._id === divisionId)
                    ? {
                      label: divisions.find((d) => d._id === divisionId).name,
                      value: divisionId,
                    }
                    : null
                }
                onChange={(selected) => setDivisionId(selected?.value || '')}
                isClearable
                styles={customSelectStyles}
              />
            </FormControl>

            <FormControl isRequired mt={4}>
              <FormLabel>District Name</FormLabel>
              <Input
                placeholder="Enter District Name"
                value={newDistrict}
                onChange={(e) => setNewDistrict(e.target.value)}
                color={inputTextColor}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter bg={modalFooterBg} borderColor="gray.200">
            <Button
              variant="ghost"
              colorScheme="red"
              mr={3}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={isEditing ? handleUpdateDistrict : handleAddDistrict}
              isLoading={loading}
            >
              <Icon as={AddIcon} mr={2} />
              {isEditing ? 'Update District' : 'Add District'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DistrictSection;
